import {
	GridColDef,
	GridFilterModel,
	GridLogicOperator,
	GridSingleSelectColDef,
	ValueOptions
} from "@mui/x-data-grid-pro";
import {
	Accordion,
	AccordionDetails,
	AccordionSummary, Button, Checkbox,
	FormControl,
	IconButton, InputLabel, ListItemText, MenuItem,
	Select,
	Stack, TextField,
	Typography
} from "@mui/material";
import {Add, DragHandle, ExpandMore, Remove} from "@mui/icons-material";
import {DragAndDrop} from "../DragAndDrop";
import {GridFilterItem} from "@mui/x-data-grid/models/gridFilterItem";
import {DatePicker} from "../input/DatePicker";
import {GlobalState} from "../../../global/GlobalState";
import React from "react";
import {translateFunction} from "../../../localization/TranslationService";

const translate = translateFunction();

interface FilterUiProps {
	readonly model: GridFilterModel;
	readonly columns: GridColDef[];
	readonly onChange?: (model: GridFilterModel) => void;
}


export function GraphQlDataGridFilter(props: FilterUiProps) {
	return (
		<Accordion key={"filter"} defaultExpanded={true}>
			<AccordionSummary expandIcon={<ExpandMore/>}>
				<Typography>{translate("Filter")}</Typography>
			</AccordionSummary>
			<AccordionDetails>
				<Stack alignItems={"start"}>
					<DragAndDrop
						items={props.model.items}
						onItemsChanged={(items) =>
							props.onChange?.call(undefined, {...props.model, items})
						}
					>
						{(item, index) => {
							const filterColumn = FilterColumn.fromModel(item, props.columns);
							
							return (
								<Stack
									key={index}
									direction={"row"}
									alignItems={"end"}
									sx={{
										"& > :not(style)": {m: "5px"},
									}}
								>
									<DragHandle/>
									<IconButton
										color={"primary"}
										onClick={() => {
											if (props.onChange) {
												const newItems = [...props.model.items];
												newItems.splice(index, 1);
												const newModel = {...props.model, items: newItems};
												props.onChange(newModel);
											}
										}}
									>
										<Remove/>
									</IconButton>
									<FormControl>
										<Select
											style={{
												width: "60px",
												visibility: index === 0 ? "hidden" : undefined,
											}}
											variant={"standard"}
											value={props.model?.logicOperator ?? "and"}
											disabled={index !== 1}
											onChange={(event) => {
												if (props.onChange) {
													const newModel = {
														...props.model,
														logicOperator: event.target
															.value as GridLogicOperator,
													};
													props.onChange(newModel);
												}
											}}
										>
											<MenuItem key={"and"} value={"and"}>
												And
											</MenuItem>
											<MenuItem key={"or"} value={"or"}>
												Or
											</MenuItem>
										</Select>
									</FormControl>
									{filterColumn.renderColumnInput({props, item, index})}
									{filterColumn.renderOperatorInput({props, item, index})}
									{filterColumn.renderValueInput({props, item, index})}
								</Stack>
							);
						}}
					</DragAndDrop>
					
					<Button
						startIcon={<Add/>}
						onClick={() => {
							if (props.onChange) {
								const columns = props.columns
									.filter((it: GridColDef) => it.field)
									.filter(
										(it: GridColDef) =>
											props.model.items
												.map((it) => it.field)
												.indexOf(it.field) === -1
									);
								const filterColumn = FilterColumn.fromColumns(columns);
								const newItems = [
									...props.model.items,
									filterColumn.toFilterModel(),
								];
								const newModel = {...props.model, items: newItems};
								props.onChange(newModel);
							}
						}}
					>
						{translate("Add Filter")}
					</Button>
				</Stack>
			</AccordionDetails>
		</Accordion>
	);
}

const operators = [
	{key: "contains", display: "contains", inputType: "text"},
	{key: "equals", display: "equals", inputType: "text"},
	{key: "not", display: "not equals", inputType: "text"},
	{key: "is", display: "is", inputType: "text"},
	{key: "isAnyOf", display: "is any of", inputType: "text[]"},
	{key: "startsWith", display: "starts with", inputType: "text"},
	{key: "endsWith", display: "ends with", inputType: "text"},
	{key: "before", display: "is before", inputType: "temporal"},
	{key: "onOrBefore", display: "is at or before", inputType: "temporal"},
	{key: "after", display: "is after", inputType: "temporal"},
	{key: "onOrAfter", display: "is at or after", inputType: "temporal"},
	{key: "isEmpty", display: "is empty", inputType: "none"},
	{key: "isNotEmpty", display: "is not empty", inputType: "none"},
];

const filterables = {
	uuid: {
		category: "uuid",
		operators: ["equals", "not"],
	},
	boolean: {
		category: "boolean",
		operators: ["equals"]
	},
	string: {
		category: "string",
		operators: [
			"contains",
			"equals",
			"not",
			"startsWith",
			"endsWith",
			"isEmpty",
			"isNotEmpty",
		],
	},
	enum: {
		category: "enum",
		operators: ["is", "isAnyOf"],
	},
	temporal: {
		category: "temporal",
		operators: [
			"before",
			"onOrBefore",
			"after",
			"onOrAfter",
			"isEmpty",
			"isNotEmpty",
		],
	},
};

interface Filterable {
	readonly category: string;
	readonly operators: string[];
}

class FilterColumn {
	constructor(
		readonly field: string,
		readonly filterable: Filterable | undefined,
		readonly valueOptions: ValueOptions[] | undefined,
		readonly operator?: string,
		readonly value?: any
	) {
	}
	
	toFilterModel(): GridFilterItem {
		return {
			field: this.field,
			operator: this.operator ?? "",
			value: this.value,
		};
	}
	
	renderColumnInput({
		props,
		item,
		index,
	}: {
		props: FilterUiProps;
		item: GridFilterItem;
		index: number;
	}) {
		return (
			<FormControl>
				<InputLabel>{translate("Column")}</InputLabel>
				<Select
					style={{minWidth: "200px"}}
					label={translate("Column")}
					variant={"standard"}
					value={item.field}
					onChange={(event) => {
						if (props.onChange) {
							const newItems = [...props.model.items];
							newItems[index] = FilterColumn.normalize(
								{
									...newItems[index],
									field: event.target.value,
								},
								props.columns
							);
							const newModel = {...props.model, items: newItems};
							props.onChange(newModel);
						}
					}}
				>
					{props.columns
						.filter(it => !!it.field)
						.filter(it => it.filterable !== false)
						.map(column => {
							return (
								<MenuItem key={column.field} value={column.field}>
									{translate(column.field)}
								</MenuItem>
							);
						})}
				</Select>
			</FormControl>
		);
	}
	
	renderOperatorInput({
		props,
		item,
		index,
	}: {
		props: FilterUiProps;
		item: GridFilterItem;
		index: number;
	}) {
		const filterable = this.filterable;
		if (!filterable) return undefined;
		
		return (
			<FormControl key={"operator"}>
				<InputLabel>{translate("Operator")}</InputLabel>
				<Select
					style={{width: "160px"}}
					label={translate("Operator")}
					variant={"standard"}
					value={
						filterable.operators.indexOf(item.operator ?? "-") >= 0
							? item.operator ?? ""
							: ""
					}
					onChange={(event) => {
						if (props.onChange) {
							const newItems = [...props.model.items];
							newItems[index] = FilterColumn.normalize(
								{
									...newItems[index],
									operator: event.target.value,
								},
								props.columns
							);
							const newModel = {...props.model, items: newItems};
							props.onChange(newModel);
						}
					}}
				>
					{operators
						.filter((it) => filterable.operators.indexOf(it.key) >= 0)
						.map((it) => (
							<MenuItem key={it.key} value={it.key}>
								{translate(it.display)}
							</MenuItem>
						))}
					;
				</Select>
			</FormControl>
		);
	}
	
	renderValueInput({
		props,
		item,
		index,
	}: {
		props: FilterUiProps;
		item: GridFilterItem;
		index: number;
	}) {
		const category = this.filterable?.category;
		
		function getValue(filterValue: any) {
			if (typeof filterValue === "object")
				return filterValue.value;
			else
				return filterValue?.toString();
		}
		
		function getLabel(filterValue: any) {
			if (typeof filterValue === "object")
				return filterValue.label;
			else
				return filterValue?.toString();
		}
		
		if (!category) return undefined;
		if (category === "boolean") {
			return <Checkbox
				checked={item.value === "true" || item.value === true}
				onChange={(event) => {
					if (props.onChange) {
						const newItems = [...props.model.items];
						newItems[index] = {
							...newItems[index],
							value: event.target.checked,
						};
						const newModel = {...props.model, items: newItems};
						props.onChange(newModel);
					}
				}}
			/>;
		}
		const operator = operators.find((it) => it.key === this.operator);
		if (!operator || operator.inputType === "none") return undefined;
		
		if (category === "string") {
			return (
				<TextField
					key={"string"}
					label={translate("Value")}
					variant={"standard"}
					value={item.value}
					style={{width: "300px"}}
					onChange={(event) => {
						if (props.onChange) {
							const newItems = [...props.model.items];
							newItems[index] = {
								...newItems[index],
								value: event.target.value,
							};
							const newModel = {...props.model, items: newItems};
							props.onChange(newModel);
						}
					}}
				/>
			);
		}
		
		if (category === "uuid") {
			return (
				<TextField
					key={"string"}
					label={translate("Value")}
					variant={"standard"}
					value={item.value}
					style={{width: "300px"}}
					onChange={(event) => {
						if (props.onChange) {
							const newItems = [...props.model.items];
							newItems[index] = {
								...newItems[index],
								value: event.target.value,
							};
							const newModel = {...props.model, items: newItems};
							props.onChange(newModel);
						}
					}}
				/>
			);
		}
		
		if (category === "enum") {
			return (
				<FormControl key={"enum"} sx={{m: 1, width: 300}}>
					<InputLabel>{translate("Value")}</InputLabel>
					<Select
						label={translate("Value")}
						variant={"standard"}
						multiple={operator.inputType.endsWith("[]")}
						value={
							(typeof item.value === "string" ? [item.value] : item.value) ?? []
						}
						onChange={(event) => {
							if (props.onChange) {
								const newItems = [...props.model.items];
								newItems[index] = {
									...newItems[index],
									value: event.target.value,
								};
								const newModel = {...props.model, items: newItems};
								props.onChange(newModel);
							}
						}}
						renderValue={(selected) => {
							
							if (this.valueOptions && this.valueOptions.length >= 1 && typeof this.valueOptions[0] === "object") {
								const selectedValues: string[] = typeof selected === "string" ? [selected] : selected;
								const selectedOptions = this.valueOptions?.filter((it: any) => selectedValues.indexOf(it.value) >= 0);
								return selectedOptions?.map((x: any) => x.label).map(translate).join(", ");
							}
							if (typeof selected === "string") return selected;
							return selected.map(translate).join(", ");
						}}
					>
						{this.valueOptions?.map((it) => {
							return (
								<MenuItem key={getValue(it)} value={getValue(it)}>
									<Checkbox checked={item.value?.indexOf(getValue(it)) >= 0}/>
									<ListItemText primary={translate(getLabel(it))}/>
								</MenuItem>
							);
						})}
					</Select>
				</FormControl>
			);
		}
		
		if (category === "temporal") {
			return (
				<DatePicker // todo date time picker
					key={"temporal"}
					label={translate("Value")}
					variant={"standard"}
					inputFormat={GlobalState.userPreferences.datePatternOrDefault}
					value={item.value}
					onValueChange={(value) => {
						if (props.onChange) {
							const newItems = [...props.model.items];
							newItems[index] = {
								...newItems[index],
								value: value?.toString(),
							};
							const newModel = {...props.model, items: newItems};
							props.onChange(newModel);
						}
					}}
				/>
			);
		}
		
		return undefined;
	}
	
	static fromColumns(columns: GridColDef[]): FilterColumn {
		if (columns.length === 0) return FilterColumn.fromColumn(undefined);
		else return FilterColumn.fromColumn(columns[0]);
	}
	
	static fromColumn(column: GridColDef | undefined): FilterColumn {
		return FilterColumn.from(undefined, column);
	}
	
	static fromModel(
		model: GridFilterItem | undefined,
		columns: GridColDef[]
	): FilterColumn {
		const column = columns.find(
			(it) => it.field !== "" && it.field === model?.field
		);
		return FilterColumn.from(model, column);
	}
	
	// todo boolean
	// todo (date) time picker
	// todo numerics
	static from(
		model: GridFilterItem | undefined,
		column: GridColDef | undefined
	): FilterColumn {
		const filterable: Filterable | undefined =
			column === undefined
				? undefined
				: column.type === "dateTime"
					? filterables.temporal
					: column.type === "singleSelect"
						? filterables.enum
						: "uuid" in column && column.uuid
							? filterables.uuid
							: column.type === "boolean"
								? filterables.boolean
								: filterables.string;
		
		const options = (column as GridSingleSelectColDef).valueOptions as ValueOptions[] | undefined;
		
		let operatorKey = model?.operator ?? filterable?.operators[0];
		if (
			operatorKey &&
			(filterable === undefined ||
				filterable.operators.indexOf(operatorKey) === -1)
		)
			operatorKey = undefined;
		
		let value = model?.value;
		
		if (options) {
			if (Array.isArray(value)) {
				value = value.filter((it: GridColDef) => options.indexOf(it) >= 0);
			} else if (typeof value === "object") {
				value = value.value.filter((it: GridColDef) => options.indexOf(it) >= 0);
			} else if (options.indexOf(value) === -1) {
				value = undefined;
			}
		}
		
		const operator = operators.find((it) => it.key === operatorKey);
		if (operator && operator.inputType.endsWith("[]")) {
			if (value === undefined || value === null) value = [];
			else if (!Array.isArray(value)) value = [value];
		} else {
			if (value && Array.isArray(value)) value = value.length > 0 ? value[0] : undefined;
		}
		
		return new FilterColumn(
			column?.field ?? "",
			filterable,
			options,
			operatorKey,
			value
		);
	}
	
	static normalize(
		model: GridFilterItem,
		columns: GridColDef[]
	): GridFilterItem {
		const instance = FilterColumn.fromModel(model, columns);
		return instance.toFilterModel();
	}
}
